import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import {
  ArrowForwardRounded,
  BarChartRounded,
  MailOutlineRounded,
} from "@mui/icons-material";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { WorkingHours } from "./WorkingHours";
import { AnomaliesReport } from "./Anomalies";
import { DailyReport } from "./DailyReport";
import { OvertimeReport } from "./OvertimeReport";
import { LateCountReport } from "./CountReport";
import { LeaveAndPermission } from "./LeaveAndPermission";
import { WorkforceReport } from "./WorkforceReport";
import { WrkedHrsReports } from "./WorkingHoursReport";
import { EsslReport } from "./EsslReport";
import { TeamEODReport } from "pages/TeamZone/EODReport";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import { DeviceStatusReport } from "./DeviceStatusReport";
import {
  PunchProcessingExceptionsReport,
  PUNCH_EXCEPTIONS_REPORT_NAME,
} from "./PunchProcessingExceptions";
import {
  designColors,
  designRadii,
  designShell,
  designTheme,
  designType,
} from "services/constants/designTokens";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { reportCategories } from "./reportRegistry";
import {
  checkViewerPermission,
  findSubModuleByName,
} from "services/utils/HelperFunction";

export const Reports = () => {
  const [reportName, setReportName] = useState("");
  const [showReports, setShowReport] = useState(false);
  const [overTimeReportScreen, setOverTimeReportScreen] = useState(false);
  const [activeCategoryKey, setActiveCategoryKey] = useState(null);
  const [reportHeaderActions, setReportHeaderActions] = useState(null);
  const history = useHistory();

  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // EOD Report needs the underlying submodule's id (passed to TeamEODReport
  // below), not just a yes/no, so it's looked up separately from every
  // other report instead of going through checkViewerPermission directly.
  // That used to mean its accessibleReportCategories check below only
  // tested whether this submodule was present at all, not whether its
  // own viewer permission was actually true — the exact "presence isn't
  // access" gap already fixed for every other report here, just missed for
  // this one case. hasEodViewerAccess closes that gap while still keeping
  // eodSubModuleId around for the prop.
  const eodSubModule = useMemo(
    () =>
      findSubModuleByName(
        userScreenAccess?.module_details,
        "EOD Report",
        "EOD Report",
      ),
    [userScreenAccess],
  );
  const eodSubModuleId = eodSubModule?.sub_module_id;
  const hasEodViewerAccess = eodSubModule?.permissions?.viewer === true;
  const { result: colorCode } = useFetch(`${domain}attendance/status-colors/`);
  const accessibleReportCategories = useMemo(
    () =>
      reportCategories
        .map((category) => {
          // Every category is now filtered, not just "daily-work" — a
          // report being present in the registry doesn't mean this role has
          // viewer access to it (same "presence isn't access" gap fixed in
          // TeamZone/Approve: a submodule can be granted to the company
          // with viewer:false for this role).
          const accessibleReports = category.reports.filter((report) => {
            if (report.reportName === "EOD Report") {
              return hasEodViewerAccess;
            }

            if (!report.reportName) {
              return true;
            }

            return checkViewerPermission(
              userScreenAccess?.module_details,
              "Reports",
              report.reportName,
            );
          });

          return {
            ...category,
            reports: accessibleReports,
          };
        })
        .filter((category) => category.reports.length > 0),
    [hasEodViewerAccess, userScreenAccess],
  );
  const accessibleReportRegistry = useMemo(
    () =>
      accessibleReportCategories.flatMap((category) =>
        category.reports.map((report) => ({
          ...report,
          categoryKey: category.key,
        })),
      ),
    [accessibleReportCategories],
  );
  const totalReports = useMemo(
    () =>
      accessibleReportCategories.reduce(
        (sum, category) => sum + category.reports.length,
        0,
      ),
    [accessibleReportCategories],
  );
  const activeCategory =
    accessibleReportCategories.find(
      (category) => category.key === activeCategoryKey,
    ) ||
    null;
  const selectedCategoryReport =
    accessibleReportRegistry.find((report) => report.reportName === reportName) ||
    null;
  const activeCategoryReportTypes = useMemo(
    () =>
      (activeCategory?.reports || [])
        .filter((report) => report.reportName)
        .map((report) => ({
          value: report.key,
          label: report.label,
        })),
    [activeCategory],
  );
  const groupedReportFilterProps =
    activeCategory && selectedCategoryReport
      ? {
          embeddedReportTypes: activeCategoryReportTypes,
          selectedReportType:
            selectedCategoryReport?.label ||
            selectedCategoryReport?.reportName ||
            null,
          onReportTypeChange: (event, data) => {
            if (!data?.label) return;
            const nextReport = activeCategory?.reports.find(
              (report) => report.label === data.label,
            );
            if (!nextReport) return;
            handleCategoryReportChange({ target: { value: nextReport.key } });
          },
        }
      : {};

  const openReport = (report) => {
    if (report.route) {
      history.push(report.route);
      return;
    }

    setShowReport(true);
    setReportName(report.reportName);
    setOverTimeReportScreen(report.reportName === "Overtime Report");
    setReportHeaderActions(null);
  };
  const openCategory = (category) => {
    const firstSelectableReport =
      category.reports.find((report) => report.reportName) || category.reports[0];

    setActiveCategoryKey(category.key);
    setReportHeaderActions(null);

    if (firstSelectableReport?.reportName) {
      setShowReport(true);
      setReportName(firstSelectableReport.reportName);
      setOverTimeReportScreen(
        firstSelectableReport.reportName === "Overtime Report",
      );
      return;
    }

    setShowReport(false);
    setReportName("");
  };
  const closeCategoryDetail = () => {
    setShowReport(false);
    setReportName("");
    setActiveCategoryKey(null);
    setOverTimeReportScreen(false);
    setReportHeaderActions(null);
  };
  const handleEmbeddedClose = (nextValue) => {
    if (nextValue === false) {
      setShowReport(false);
      return;
    }

    setShowReport(nextValue);
  };
  const handleCategoryReportChange = (event) => {
    const nextKey = event.target.value;
    const nextReport = activeCategory?.reports.find(
      (report) => report.key === nextKey,
    );

    if (!nextReport) return;

    if (nextReport.route) {
      history.push(nextReport.route);
      return;
    }

    setReportName(nextReport.reportName);
    setShowReport(true);
    setOverTimeReportScreen(nextReport.reportName === "Overtime Report");
    setReportHeaderActions(null);
  };

  const renderComponent = () => {
    if (!reportName) return null;

    if (
      reportName !== "Attendance Anomalies Report" &&
      reportName !== "Daily Report" &&
      reportName !== "Overtime Report" &&
      reportName !== "Late Report" &&
      reportName !== "Leave and Permission Reports" &&
      reportName !== "Employee Attendance Report" &&
      reportName !== "Workforce Status Report" &&
      reportName !== "Working Hours Report" &&
      reportName !== "Device Status Report" &&
      reportName !== PUNCH_EXCEPTIONS_REPORT_NAME &&
      reportName !== "EOD Report"
    ) {
      return (
        <WorkingHours
          setShowReport={handleEmbeddedClose}
          reportName={reportName}
          colorCode={colorCode}
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "Attendance Anomalies Report") {
      return (
        <AnomaliesReport
          setShowReport={handleEmbeddedClose}
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "Daily Report") {
      return (
        <DailyReport
          setShowReport={handleEmbeddedClose}
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "Overtime Report") {
      return (
        <OvertimeReport
          setShowReport={handleEmbeddedClose}
          overTimeReportScreen={overTimeReportScreen}
          setOverTimeReportScreen={setOverTimeReportScreen}
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "Late Report") {
      return <LateCountReport setShowReport={handleEmbeddedClose} />;
    } else if (reportName === "Leave and Permission Reports") {
      return (
        <LeaveAndPermission
          setShowReport={handleEmbeddedClose}
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "Employee Attendance Report") {
      return (
        <EsslReport
          setShowReport={handleEmbeddedClose}
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "Workforce Status Report") {
      return (
        <WorkforceReport
          setShowReport={handleEmbeddedClose}
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "Working Hours Report") {
      return (
        <WrkedHrsReports
          setShowReport={handleEmbeddedClose}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "Device Status Report") {
      return (
        <DeviceStatusReport
          setShowReport={handleEmbeddedClose}
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === PUNCH_EXCEPTIONS_REPORT_NAME) {
      return (
        <PunchProcessingExceptionsReport
          setReportHeaderActions={setReportHeaderActions}
          {...groupedReportFilterProps}
        />
      );
    } else if (reportName === "EOD Report") {
      return (
        <TeamEODReport
          embedded={true}
          setReportHeaderActions={setReportHeaderActions}
          subModuleId={eodSubModuleId}
        />
      );
    }
    return null;
  };

  return (
    <ThemeProvider theme={designTheme}>
      {activeCategory ? (
        <Mui.Box
          sx={{
            ...designShell.pageSurface,
            px: { xs: 1.5, md: 2 },
            py: { xs: 2, md: 2.5 },
          }}
        >
          <Mui.Stack spacing={2}>
            <Mui.Stack
              direction={{ xs: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={2}
              sx={{ width: "100%" }}
            >
              <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                <GlobalBreadcrumb
                  sx={{ mb: 0.9 }}
                  trail={[
                    {
                      label: "Team Workspace",
                      onClick: () => history.push("/admin/teamzone/home"),
                    },
                    {
                      label: "Reports",
                      onClick: closeCategoryDetail,
                    },
                    ...(showReports
                      ? [
                          `${activeCategory.title} / ${
                            selectedCategoryReport?.label ||
                            selectedCategoryReport?.reportName ||
                            "Report"
                          }`,
                        ]
                      : [activeCategory.title]),
                  ]}
                />
                <Mui.Typography sx={designType.pageTitle}>
                  {showReports
                    ? selectedCategoryReport?.label ||
                      selectedCategoryReport?.reportName ||
                      activeCategory.title
                    : activeCategory.title}
                </Mui.Typography>
                <Mui.Typography sx={{ ...designType.pageSubtitle, maxWidth: 820 }}>
                  {showReports
                    ? selectedCategoryReport?.description ||
                      `View ${selectedCategoryReport?.label || selectedCategoryReport?.reportName || "report"} under ${activeCategory.title}.`
                    : activeCategory.description}
                </Mui.Typography>
              </Mui.Box>
              {showReports ? (
                <Mui.Box
                  sx={{
                    width: { xs: "100%", lg: "auto" },
                    display: "flex",
                    justifyContent: { xs: "flex-start", lg: "flex-end" },
                    flexShrink: 0,
                  }}
                >
                  {reportHeaderActions}
                </Mui.Box>
              ) : null}
            </Mui.Stack>

            {showReports ? renderComponent() : null}
          </Mui.Stack>
        </Mui.Box>
      ) : (
        <Mui.Box
          sx={{
            ...designShell.pageSurface,
            px: { xs: 1.5, md: 2 },
            py: { xs: 2, md: 2.5 },
          }}
        >
          <Mui.Stack spacing={2.5}>
            <Mui.Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Mui.Box>
                <GlobalBreadcrumb
                  sx={{ mb: 0.9 }}
                  trail={[
                    {
                      label: "Team Workspace",
                      onClick: () => history.push("/admin/teamzone/home"),
                    },
                    "Reports",
                  ]}
                />
                <Mui.Stack direction="row" spacing={1.5} alignItems="center">
                  <Mui.Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, #E7FAF5 0%, #F5FBF8 100%)",
                      color: designColors.tealDark,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <BarChartRounded sx={{ fontSize: 34 }} />
                  </Mui.Box>
                  <Mui.Box>
                    <Mui.Typography sx={designType.pageTitle}>
                      Reports
                    </Mui.Typography>
                    <Mui.Typography sx={{ ...designType.pageSubtitle, maxWidth: 780 }}>
                      Choose a report category to view grouped insights and open the
                      related report screens from one source of truth.
                    </Mui.Typography>
                  </Mui.Box>
                </Mui.Stack>
              </Mui.Box>

              <Mui.Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: designRadii.pill,
                  border: `1px solid ${designColors.slate200}`,
                  backgroundColor: designColors.white,
                }}
              >
                <Mui.Typography sx={designType.cardTitle}>
                  {totalReports} grouped reports
                </Mui.Typography>
              </Mui.Box>
            </Mui.Stack>

            <Mui.Box
              sx={{
                borderBottom: `1px solid ${designColors.slate200}`,
                pb: 1.25,
              }}
            >
              <Mui.Typography sx={designType.sectionHeading}>
                Report Categories
              </Mui.Typography>
              <Mui.Box
                sx={{
                  mt: 1,
                  width: 42,
                  height: 4,
                  borderRadius: designRadii.pill,
                  backgroundColor: designColors.teal,
                }}
              />
            </Mui.Box>

            <Mui.Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2.5,
              }}
            >
              {accessibleReportCategories.map((category) => {
                const CategoryIcon = category.icon;

                return (
                  <Mui.Box
                    key={category.key}
                    sx={{
                      ...designShell.sectionCard,
                      borderColor: category.borderColor,
                      background: `linear-gradient(180deg, ${category.softAccent}66 0%, ${designColors.white} 34%)`,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 320,
                      cursor: "pointer",
                    }}
                    onClick={() => openCategory(category)}
                  >
                    <Mui.Box sx={{ p: 2.25, pb: 1.5 }}>
                      <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Mui.Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: "50%",
                            backgroundColor: category.softAccent,
                            color: category.accent,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CategoryIcon sx={{ fontSize: 30 }} />
                        </Mui.Box>
                        <Mui.Box>
                          <Mui.Typography
                            sx={{
                              ...designType.categoryCardTitle,
                              color: category.accent,
                            }}
                          >
                            {category.title}
                          </Mui.Typography>
                          <Mui.Typography
                            sx={{ mt: 0.75, ...designType.categoryCardDescription }}
                          >
                            {category.description}
                          </Mui.Typography>
                        </Mui.Box>
                      </Mui.Stack>
                    </Mui.Box>

                    <Mui.Box
                      sx={{
                        mx: 2.25,
                        borderTop: `1px solid ${designColors.slate200}`,
                      }}
                    />

                    <Mui.Box sx={{ px: 2.25, py: 1.25, flexGrow: 1 }}>
                      <Mui.Stack spacing={0.4}>
                        {category.reports.map((report) => (
                          <Mui.ButtonBase
                            key={report.key}
                            onClick={(event) => {
                              event.stopPropagation();
                              openCategory(category);
                              if (report.reportName) {
                                openReport(report);
                              } else if (report.route) {
                                history.push(report.route);
                              }
                            }}
                            sx={{
                              width: "100%",
                              justifyContent: "space-between",
                              textAlign: "left",
                              px: 0.25,
                              py: 0.9,
                              borderRadius: 1.5,
                              "&:hover": {
                                backgroundColor: `${category.accent}0D`,
                              },
                            }}
                          >
                            <Mui.Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <Mui.Box
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  backgroundColor: category.accent,
                                  flexShrink: 0,
                                }}
                              />
                              <Mui.Typography
                                sx={{
                                  ...designType.reportLinkLabel,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {report.label}
                              </Mui.Typography>
                            </Mui.Stack>
                            <ArrowForwardRounded
                              sx={{ fontSize: 18, color: category.accent, flexShrink: 0 }}
                            />
                          </Mui.ButtonBase>
                        ))}
                      </Mui.Stack>
                    </Mui.Box>

                    <Mui.Box
                      sx={{
                        px: 2.25,
                        py: 1.4,
                        borderTop: `1px solid ${designColors.slate200}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Mui.Typography
                        sx={{ ...designType.categoryCountLabel, color: category.accent }}
                      >
                        {category.reports.length} Reports
                      </Mui.Typography>
                      <Mui.IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          openCategory(category);
                        }}
                        size="small"
                        sx={{
                          color: category.accent,
                          backgroundColor: `${category.accent}12`,
                          "&:hover": { backgroundColor: `${category.accent}18` },
                        }}
                      >
                        <ArrowForwardRounded fontSize="small" />
                      </Mui.IconButton>
                    </Mui.Box>
                  </Mui.Box>
                );
              })}
            </Mui.Box>

            <Mui.Box
              sx={{
                ...designShell.sectionCard,
                p: { xs: 2, md: 2.25 },
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Mui.Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: `1px solid ${designColors.slate200}`,
                    color: designColors.tealDark,
                    backgroundColor: designColors.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MailOutlineRounded sx={{ fontSize: 18 }} />
                </Mui.Box>
                <Mui.Box>
                  <Mui.Typography sx={designType.calloutTitle}>
                    Can&apos;t find the report you&apos;re looking for?
                  </Mui.Typography>
                  <Mui.Typography sx={{ mt: 0.5, ...designType.calloutText }}>
                    This grouped landing page now covers Team Zone reports, working
                    hours report types and daily-work routing from one place.
                  </Mui.Typography>
                </Mui.Box>
              </Mui.Stack>
              <SecondaryButton
                sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 190 } }}
                onClick={() => history.push("/admin/teamzone/eodreport")}
              >
                Open Daily Work
              </SecondaryButton>
            </Mui.Box>
          </Mui.Stack>
        </Mui.Box>
      )}
    </ThemeProvider>
  );
};
